import { BarChart } from "@mui/x-charts/BarChart";
import { ChartsLoadingOverlay } from "@mui/x-charts/ChartsOverlay";
import { useMemo } from "react";
import { PersistentRecordingTaskStatistics } from "types/PersistentRecordingTaskStatistics";

export interface StatusTotalsBarChartSeries<TStatus extends string> {
    status: TStatus;
    label: string;
    color: string;
}

export interface StatusTotalsBarChartProps<TStatus extends string> {
    dossierDownloadStatusStatistics: PersistentRecordingTaskStatistics<TStatus>;
    series: StatusTotalsBarChartSeries<TStatus>[];
    completedStatus: TStatus;
    title: string;
}

export function StatusTotalsBarChart<TStatus extends string>(
    props: StatusTotalsBarChartProps<TStatus>,
) {
    const dataset = useMemo(() => {
        const result: Record<TStatus, number> = {} as Record<TStatus, number>;

        props.series.forEach(({ status }) => {
            result[status] =
                props.dossierDownloadStatusStatistics.statistics[status]
                    ?.percentageOfDurationDossier || 0;
        });

        return [result];
    }, [props.dossierDownloadStatusStatistics, props.series]);

    const completedStatus =
        props.dossierDownloadStatusStatistics.statistics[props.completedStatus];
    const percentageCompleted = Math.floor(
        (completedStatus?.percentageOfDurationDossier || 0) * 100,
    );

    const getTotalRecordingsWithStatus = (status: TStatus) => () =>
        status in props.dossierDownloadStatusStatistics.statistics
            ? props.dossierDownloadStatusStatistics.statistics[
                  status
              ].totalRecordings.toString()
            : "0";

    return (
        <BarChart
            layout="horizontal"
            xAxis={[{ position: "none" }]}
            yAxis={[{ position: "none", data: [props.title] }]}
            hideLegend
            localeText={{
                // we (mis)use the loading overlay to show the percentage completed
                loading: percentageCompleted + "%",
            }}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            dataset={dataset}
            series={props.series.map(({ status, label, color }) => ({
                label,
                dataKey: status,
                stack: "total",
                color,
                stackOffset: "none",
                id: status,
                valueFormatter: getTotalRecordingsWithStatus(status),
            }))}
        >
            <ChartsLoadingOverlay />
        </BarChart>
    );
}
